<script setup lang="ts">
import { DateTime } from 'luxon'
import { loadGoogleAPI } from '~/lib/clients/googleMaps'
import { RestaurantAreas, RestaurantScheduleTypes } from '~/lib/interfaces/restaurant'
import { RestaurantMDWDetailResponse } from '~/lib/models/async-data'
import { McdViewError } from '~/lib/models/errors'
import { GTMContentGroup } from '~/lib/models/gtm'
import { Restaurant } from '~/lib/services/store/restaurant/restaurant.dto'
import { getRestaurantCodeFromSlug } from '~/utils/restaurantUtils'

definePageMeta({
  alias: ['/restaurantes/:citySlug/:restaurantSlug'],
  pageType: 'RESTAURANT_DETAIL',
})
const { t } = useLocale()
const route = useRoute()
const countryCode = useCountry()
const { middlewareServiceRef, restaurantServiceRef } = useStoreService()
const isMobile = useIsMobile()
const appStore = useAppStore()
const restaurantActions = useRestaurantActions()
const mcdRoute = useMcdRoutes()
const { trackPageView, trackSelectRestaurant } = useGTM()
const errorView = useErrorView()
const middlewareService = middlewareServiceRef()
const restaurantService = restaurantServiceRef()

const getIconUrl = (text: string) => {
  return '/images/icons/' + text + '.svg'
}

const getIconUrlArea = (text: string) => {
  return '/images/icons/area/' + text + '.svg'
}

const { restaurantSlug } = getRouteParams(route.params, ['restaurantSlug'])
const { citySlug } = getRouteParams(route.params, ['citySlug'])

const restaurantCode = getRestaurantCodeFromSlug({ country: countryCode, slug: restaurantSlug })

const { data, error } = await useWrapperAsyncData<RestaurantMDWDetailResponse>(() =>
  middlewareService.fetchRestaurantDetail({
    countryCode,
    restaurantCode,
  })
)

if (error.value || !data.value?.length) {
  errorView.value = new McdViewError(error.value?.message || 'Restaurant detail not found', 404)
}

const restaurant = data.value?.[0]

const schedule = restaurant
  ? getGlobalScheduleHoursByDay(restaurant.name, restaurant.generalHour)
  : undefined

const state = restaurant
  ? getRestaurantOpenState({
      datetime: DateTime.utc(),
      active: restaurant.active,
      timezone: restaurant.timezone,
      code: restaurant.code,
      timePeriods: restaurant.timeSlotsService,
      pickupTime: 0,
    })
  : undefined

const restaurantPosition = {
  lat: restaurant?.coordinates.latitude || 0,
  lng: restaurant?.coordinates.longitude || 0,
}

const { distanceFromUser, userCoordinates } = useUserLocation()

const services = restaurant
  ? Object.entries(restaurant.services).sort(
      ([, valueA], [, valueB]) => Number(valueB) - Number(valueA)
    )
  : undefined

// Leaving this here for future reference. Should remove once backend returns images

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const restaurantImages = [
  {
    url: 'https://images.unsplash.com/photo-1623156346149-d5cec8b29818?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1931&q=80',

    width: 1100,
    height: 622,
  },
  {
    url: 'https://images.unsplash.com/photo-1606720335177-3d04e70fb13b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80',
    width: 1100,
    height: 622,
  },
  {
    url: 'https://images.unsplash.com/photo-1610736427087-9c93ff8c9476?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    width: 1100,
    height: 622,
  },
  {
    url: 'https://images.unsplash.com/photo-1610736427087-9c93ff8c9476?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    width: 1100,
    height: 622,
  },
]

const isGoogleLoaded = ref(false)
const localization = useConfiguration('localization')

const selectRestaurant = async () => {
  if (!restaurant) return
  let restaurantDetail: Restaurant | undefined
  try {
    restaurantDetail = await restaurantService.getStoreRestaurantByCode({
      countryCode,
      area: RestaurantAreas.MOP,
      restaurantCode: restaurant.code,
    })
  } catch (error) {
    return
  }

  trackSelectRestaurant(restaurantDetail.code)
  await restaurantActions.setRestaurant(restaurantDetail)
}

useJsonld(() => {
  if (!restaurant) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    'name': restaurant.name,
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': restaurant.address,
      'addressLocality': restaurant.city,
      'addressRegion': restaurant.code,
      'postalCode': restaurant?.cep || '',
      'addressCountry': countryCode,
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': restaurant.coordinates.latitude,
      'longitude': restaurant.coordinates.longitude,
    },
    'OpeningHoursSpecification': selectedRestaurantSchedule(schedule),
  }
})

onMounted(async () => {
  if (process.client) {
    if (restaurant) {
      const contentGroup3 = GTMContentGroup.tertiary.RESTAURANT_DETAIL(restaurant.code)
      trackPageView({
        cg: GTMContentGroup.primary.ECOMMERCE,
        cg2: GTMContentGroup.secondary.LOCATOR,
        cg3: contentGroup3,
      })
    }
    const apiKey = getOverridenGoogleMapsKey() || localization?.googleMapsKey
    await loadGoogleAPI({ apiKey, region: localization?.country })

    isGoogleLoaded.value = true
  }
})
</script>

<template>
  <LazyErrorView v-if="errorView" :error="errorView" />
  <template v-else-if="restaurant && state">
    <div v-if="isMobile" class="flex gap-2 px-2 pb-2 pt-3 text-lg font-bold">
      <img
        class="flex-start h-8 cursor-pointer md:h-6"
        src="/images/icons/arrowLeft.svg"
        alt="back"
        @click="mcdRoute.navigateToCityRestaurantList(citySlug)"
      />
      <p>{{ t('selectRestaurant.MOP.findYourMcDonalds') }}</p>
    </div>

    <div class="mx-auto mt-4 w-full md:mt-20">
      <div class="mx-auto max-w-[1072px]">
        <h1
          v-if="!isMobile"
          class="max-w-[376px] text-[32px] font-bold leading-9 tracking-[-0.15px]"
        >
          {{ restaurant.name }}
        </h1>
      </div>

      <div class="flex w-full justify-center gap-24 md:my-8">
        <div class="mx-4 flex h-full w-full flex-col gap-8 md:mx-0 md:max-w-[376px]">
          <div class="mcd-custom-scrollbar flex flex-col">
            <div class="flex w-full flex-col gap-1">
              <RestaurantName
                v-if="isMobile"
                :name="restaurant.name"
                :city="restaurant.city"
                :show-if-favorite="isMobile ? true : false"
                :restaurant-code="restaurant.code"
                :heading="true"
              />
              <RestaurantDirection
                :address="restaurant.address"
                :city="restaurant.city"
                :distance-from-user="isMobile ? distanceFromUser(restaurantPosition) : undefined"
                :show-if-favorite="isMobile ? false : true"
                :restaurant-code="restaurant.code"
              />
              <RestaurantState :restaurant="restaurant" />
              <ClientOnly>
                <HowToArrive
                  :distance="distanceFromUser(restaurantPosition)"
                  :latitude="restaurant.coordinates.latitude"
                  :longitude="restaurant.coordinates.longitude"
                  :user-location="userCoordinates"
                />
              </ClientOnly>
            </div>
          </div>
          <McdButton
            v-if="!appStore.isEcommerceDisabled && !isMobile"
            is-primary
            @click="selectRestaurant"
          >
            {{ t('common.startOrder') }}
          </McdButton>
          <!-- <ImageGallery
          v-if="isMobile"
          gallery-i-d="my-test-gallery"
          :images-data="restaurantImages"
        /> -->
          <!-- //styleName: Body/Large Body 02;
font-family: Speedee;
font-size: 14px;
font-weight: 700;
line-height: 16px;
letter-spacing: -0.15000000596046448px;
text-align: left; -->

          <div class="flex flex-col gap-1">
            <h2
              class="mb-2 text-sm font-bold leading-4 tracking-[-0.15px] md:text-[32px] md:leading-9"
            >
              {{ t('restaurant.detail.schedule') }}
            </h2>
            <div v-for="(e, idx) of schedule" :key="idx">
              <div class="flex justify-between text-xs text-mcd-secondaryDarkGrey">
                <p>
                  {{ t(`common.${e.day}`) }}
                </p>
                <p>
                  {{ e.schedule || t('restaurant.detail.closed') }}
                </p>
              </div>
            </div>
          </div>
          <div>
            <h2
              class="mb-2 text-sm font-bold leading-4 tracking-[-0.15px] md:text-[32px] md:leading-9"
            >
              {{ t('restaurant.detail.mop') }}
            </h2>
            <div class="grid grid-cols-2">
              <template v-for="([key, value], idx) of Object.entries(state)" :key="idx">
                <div
                  class="flex items-center gap-2 text-xs"
                  :class="value === RestaurantScheduleTypes.CLOSED ? 'opacity-50' : 'opacity-100'"
                >
                  <img
                    :src="getIconUrlArea(key)"
                    :alt="key + 'icon'"
                    class="h-6 fill-white text-white"
                  />
                  {{ t(`common.areas.${key}`) }}
                </div>
              </template>
            </div>
          </div>

          <div>
            <h2
              class="mb-2 text-sm font-bold leading-4 tracking-[-0.15px] md:text-[32px] md:leading-9"
            >
              {{ t('restaurant.detail.services.title') }}
            </h2>
            <div class="grid grid-cols-2">
              <template v-for="([key, value], idx) of services" :key="idx">
                <div
                  class="flex items-center gap-2 text-xs"
                  :class="value === false ? 'opacity-50' : 'opacity-100'"
                >
                  <img
                    :src="getIconUrl(key)"
                    :alt="key + 'icon'"
                    class="h-6 fill-white text-white"
                  />
                  {{ t(`restaurant.detail.services.${key}`) }}
                </div>
              </template>
            </div>
          </div>

          <McdButton
            v-if="isMobile && !appStore.isEcommerceDisabled"
            class="mb-6"
            is-primary
            @click="selectRestaurant"
          >
            {{ t('common.startOrder') }}
          </McdButton>
        </div>

        <div v-if="!isMobile" class="flex w-[600px] flex-col gap-4">
          <div class="h-60 overflow-hidden rounded-lg">
            <GMapMap
              v-if="isGoogleLoaded"
              :options="{
                zoomControl: false,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                disableDefaultUi: true,
              }"
              :center="restaurantPosition"
              :zoom="13"
              class="h-full"
            >
              <GMapMarker
                :position="restaurantPosition"
                :icon="{
                  url: '/images/icons/restaurantPin.svg',
                }"
              />
            </GMapMap>
          </div>

          <!-- <ImageGallery gallery-i-d="my-test-gallery" :images-data="restaurantImages" /> -->
        </div>
      </div>
    </div>
  </template>
</template>
